/** Root path every routed screen lives under - see pages.routing.ts. */
const PAGES_PREFIX = 'pages';

/**
 * Canonical form of an app url, used as a desktop window's identity so the same screen reached
 * two different ways resolves to one window.
 *
 * Call sites are inconsistent in ways that don't mean "a different screen": some omit the
 * leading slash (`navigateByUrl('pages/patientAccounting/approvalFollowUp/')`), and many build
 * urls by concatenation and end up with a trailing slash. Query string and fragment are left
 * untouched - those *do* distinguish screens.
 *
 * Repeated inner slashes are deliberately NOT collapsed. On a parameterized screen a `//` means
 * one of the interpolated params came out empty, and collapsing it would shift every later
 * param up one position - turning a visibly broken screen into a silently wrong one.
 */
export function normalizeAppUrl(url: string): string {
    if (!url) {
        return '';
    }
    const trimmed = url.trim();
    const suffixIndex = indexOfFirst(trimmed, ['?', '#']);
    const path = suffixIndex === -1 ? trimmed : trimmed.slice(0, suffixIndex);
    const suffix = suffixIndex === -1 ? '' : trimmed.slice(suffixIndex);
    const withLeadingSlash = path.startsWith('/') ? path : '/' + path;
    const trimmedPath = withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
    return trimmedPath + suffix;
}

/**
 * Path segments of `url` below `/pages`, with matrix params stripped, or `null` when `url` isn't
 * a `/pages` url at all. `/pages` itself yields an empty array.
 */
export function pagesSegments(url: string): string[] | null {
    const path = normalizeAppUrl(url).split('?')[0].split('#')[0];
    const segments = splitPath(path).map(segment => segment.split(';')[0]);
    if (segments[0] !== PAGES_PREFIX) {
        return null;
    }
    return segments.slice(1);
}

/**
 * Human-readable window title for a url that has no PagesMenu node of its own. Parameterized
 * screens (e.g. `/pages/patientAccounting/billing/transactions/1596058/139791/2`) aren't in
 * PagesMenu at all, so the screen's own path segment is the closest thing to a name it has:
 * "transactions" -> "Transactions", "todayCashBox" -> "Today Cash Box".
 */
export function titleFromUrl(url: string): string {
    const last = screenSegment(pagesSegments(url) || []);
    if (!last) {
        return 'Screen';
    }
    return last
        .replace(/[-_]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, first => first.toUpperCase());
}

/**
 * The route params trailing a screen's path, as they appear in the url -
 * `/pages/patientAccounting/billing/transactions/1596058/139791/2` -> `1596058/139791/2`.
 *
 * Used to tell apart two windows on the same screen: without it, three Transactions windows for
 * three different patients are all just titled "Transactions". Defined as everything after the
 * last non-numeric segment, so it agrees with titleFromUrl() about where the screen name ends and
 * never echoes a path segment that is already in the title (`.../inventoryTransferStation/new`
 * has no params by this definition, which is the intent - "new" is part of the screen, not a record
 * identifier).
 */
export function paramsFromUrl(url: string): string {
    const segments = pagesSegments(url) || [];
    const screen = screenSegment(segments);
    if (!screen) {
        return '';
    }
    return segments.slice(segments.lastIndexOf(screen) + 1).join('/');
}

/** Last segment that isn't a bare number - the one naming the screen itself. */
function screenSegment(segments: string[]): string | undefined {
    const named = segments.filter(segment => !/^\d+$/.test(segment));
    return named[named.length - 1];
}

function splitPath(path: string): string[] {
    return path.split('/').filter(segment => segment.length > 0);
}

function indexOfFirst(value: string, needles: string[]): number {
    return needles
        .map(needle => value.indexOf(needle))
        .filter(index => index !== -1)
        .reduce((lowest, index) => (lowest === -1 || index < lowest ? index : lowest), -1);
}
